import type {
  Client,
  Contract,
  Invoice,
  InvoiceLine,
  LedgerAccount,
  LedgerBalance,
  LedgerEntry,
  Member,
  Payment,
  PrepayAccount,
  PrepayBalance,
  ReconException,
  Stats,
  Technician,
  TechnicianCostRate,
  TenantInvite,
  TenantSettings,
  TimeEntry,
  Timesheet,
  UnbilledEntry,
} from '../types';
import { ApiError, createClient, type Client as EngineClient, type Paged, type TokenProvider } from '../engine/api/client';

// LEGACY FAÇADE. Named endpoint helpers for the pages that predate the engine. There is no
// request logic here: every call goes through the engine client (web/src/engine/api/client.ts,
// the only place that calls fetch). New code uses `client.resource(name)` / engine hooks;
// this surface shrinks as pages migrate to ScreenDefs.

export { ApiError };
export type { Paged, TokenProvider };

export interface PageOpts {
  limit?: number;
  cursor?: string | null;
}

export function createApi(getToken: TokenProvider, onUnauthorized?: () => void) {
  const client = createClient(getToken, { onUnauthorized });

  /** Legacy signature: a fetch-style RequestInit whose body is already a JSON string. */
  function request<T>(path: string, init?: RequestInit): Promise<T> {
    if (!init) return client.request<T>(path);
    const { body, headers, ...rest } = init;
    const h = { ...(headers as Record<string, string> | undefined) };
    if (typeof body === 'string' && !h['content-type']) h['content-type'] = 'application/json';
    return client.request<T>(path, { ...(rest as Omit<RequestInit, 'body' | 'headers'>), headers: h, raw: body ?? undefined });
  }
  const post = <T>(path: string, body?: unknown) => client.request<T>(path, { method: 'POST', body: body ?? {} });
  const patch = <T>(path: string, body: unknown) => client.request<T>(path, { method: 'PATCH', body });
  const del = <T>(path: string) => client.request<T>(path, { method: 'DELETE' });
  const page = (opts?: PageOpts) => ({ limit: opts?.limit, cursor: opts?.cursor ?? null });

  return {
    /** The engine client — the one true request path. */
    client,
    request,

    // ── Invoices ──────────────────────────────────────────────────────────
    listInvoices: (opts?: PageOpts) => client.resource<Invoice>('invoices').list(page(opts)),
    getInvoice: (id: string) => client.resource<Invoice>('invoices').get(id),
    listInvoicePayments: (id: string) => client.request<{ data: Payment[] }>(`/v1/invoices/${id}/payments`),
    recordPayment: (id: string, body: { amount_minor: number; paid_at?: string; method?: string; reference?: string }) => post<Payment>(`/v1/invoices/${id}/payments`, body),
    reversePayment: (invoiceId: string, paymentId: string, reason?: string) => post<Payment>(`/v1/invoices/${invoiceId}/payments/${paymentId}/reverse`, { reason }),
    getInvoiceLines: (id: string) => client.request<{ data: InvoiceLine[] }>(`/v1/invoices/${id}/lines`),
    createInvoice: (body: { billing_party_id: string; contract_id?: string; currency?: string }) => client.resource<Invoice, typeof body>('invoices').create(body),
    addInvoiceLine: (id: string, body: { description: string; net_minor: number; quantity?: number; unit_price_minor?: number; tax_minor?: number }) => post<InvoiceLine>(`/v1/invoices/${id}/lines`, body),
    updateInvoiceLine: (id: string, lineId: string, body: Partial<{ description: string; quantity: number; unit_price_minor: number; net_minor: number; tax_minor: number }>) => patch<InvoiceLine>(`/v1/invoices/${id}/lines/${lineId}`, body),
    deleteInvoiceLine: (id: string, lineId: string) => del<{ deleted: boolean }>(`/v1/invoices/${id}/lines/${lineId}`),
    issueInvoice: (id: string, issue_date?: string) => client.resource<Invoice>('invoices').action<Invoice>('issue', { id, body: { issue_date } }),
    freezeInvoice: (id: string) => client.resource<Invoice>('invoices').action<Invoice>('freeze', { id }),
    voidInvoice: (id: string, reason?: string) => client.resource<Invoice>('invoices').action<Invoice>('void', { id, body: { reason } }),
    creditNoteInvoice: (id: string, reason?: string) => client.resource<Invoice>('invoices').action<Invoice>('credit-note', { id, body: { reason } }),
    invoiceFromTime: (body: { billing_party_id: string; time_entry_ids: string[] }) => client.resource<Invoice>('invoices').action<Invoice>('from-time', { body }),

    // ── Clients ───────────────────────────────────────────────────────────
    listClients: (opts?: PageOpts) => client.resource<Client>('clients').list(page(opts)),
    getClient: (id: string) => client.resource<Client>('clients').get(id),
    createClient: (body: { name: string; currency?: string }) => client.resource<Client, typeof body>('clients').create(body),
    updateClient: (id: string, body: Partial<Pick<Client, 'name' | 'status'>>) => client.resource<Client, Partial<Client>, typeof body>('clients').update(id, body),

    // ── Technicians ───────────────────────────────────────────────────────
    listTechnicians: (opts?: PageOpts) => client.resource<Technician>('technicians').list(page(opts)),
    createTechnician: (body: { name: string; email?: string; employment?: 'employee' | 'contractor'; cost_rate_minor?: number; default_bill_rate_minor?: number; weekly_capacity_minutes?: number }) =>
      client.resource<Technician, typeof body>('technicians').create(body),
    // Self-service: attach the signed-in login to its technician (matched by email, else created).
    linkMeToTechnician: () => post<{ outcome: string }>('/v1/technicians/link-me'),
    listTechnicianCostRates: (id: string) => client.request<TechnicianCostRate[]>(`/v1/technicians/${id}/cost-rates`),
    addTechnicianCostRate: (id: string, body: { effective_from: string; cost_rate_minor: number }) => post<unknown>(`/v1/technicians/${id}/cost-rates`, body),

    // ── Contracts + prepay ────────────────────────────────────────────────
    listContracts: (opts?: PageOpts) => client.resource<Contract>('contracts').list(page(opts)),
    getContract: (id: string) => client.resource<Contract>('contracts').get(id),
    createContract: (body: { billing_party_id: string; client_id?: string; ref?: string; cycle?: string; period_charge_minor?: number; currency?: string; start_date?: string; end_date?: string }) =>
      client.resource<Contract, typeof body>('contracts').create(body),
    listPrepayAccounts: () => client.request<{ data: PrepayAccount[] }>('/v1/prepay-accounts'),
    getPrepayAccount: (id: string) => client.resource<PrepayAccount>('prepay-accounts').get(id),
    createPrepayAccount: (body: { contract_id: string; unit?: 'hours' | 'money'; expiry_months?: number }) => client.resource<PrepayAccount, typeof body>('prepay-accounts').create(body),
    getPrepayBalance: (id: string) => client.request<PrepayBalance>(`/v1/prepay-accounts/${id}/balance`),
    topUpPrepay: (id: string, body: { quantity?: number; amount_minor?: number; occurred_on?: string; note?: string }) => post<PrepayBalance>(`/v1/prepay-accounts/${id}/top-up`, body),

    // ── Timesheets + time entries ─────────────────────────────────────────
    listTimesheets: (opts?: PageOpts) => client.resource<Timesheet>('timesheets').list(page(opts)),
    getTimesheet: (id: string) => client.resource<Timesheet>('timesheets').get(id),
    getTimesheetEntries: (id: string) => client.request<{ data: TimeEntry[] }>(`/v1/timesheets/${id}/entries`),
    submitTimesheet: (id: string) => post<Timesheet>(`/v1/timesheets/${id}/submit`),
    approveTimesheet: (id: string) => post<Timesheet>(`/v1/timesheets/${id}/approve`),
    lockTimesheet: (id: string, by?: string) => post<Timesheet>(`/v1/timesheets/${id}/lock`, { by }),
    listTimeEntries: () => client.request<{ data: TimeEntry[] }>('/v1/time-entries'),
    createTimeEntry: (body: Record<string, unknown>) => client.resource<TimeEntry, Record<string, unknown>>('time-entries').create(body),
    updateTimeEntry: (id: string, body: Record<string, unknown>) => client.resource<TimeEntry, Record<string, unknown>, Record<string, unknown>>('time-entries').update(id, body),
    listUnbilledTime: () => client.request<{ data: UnbilledEntry[] }>('/v1/time-entries/unbilled'),

    // ── Tenant settings ───────────────────────────────────────────────────
    getSettings: () => client.request<TenantSettings>('/v1/settings'),
    updateSettings: (body: Partial<TenantSettings>) => patch<TenantSettings>('/v1/settings', body),

    // ── Ledger + reconciliation ───────────────────────────────────────────
    listLedgerAccounts: (opts?: PageOpts) => client.resource<LedgerAccount>('ledger-accounts').list(page(opts)),
    getLedgerAccount: (id: string) => client.resource<LedgerAccount>('ledger-accounts').get(id),
    getLedgerEntries: (id: string, limit = 200) => client.request<{ data: LedgerEntry[] }>(`/v1/ledger-accounts/${id}/entries`, { query: { limit } }),
    getLedgerBalance: (id: string, asOf?: string) => client.request<LedgerBalance>(`/v1/ledger-accounts/${id}/balance`, { query: { as_of: asOf } }),
    verifyLedgerChain: (id: string) => post<{ ok: boolean; entries: string; broken_seq: string | null; error?: string }>(`/v1/ledger-accounts/${id}/verify`),
    listReconExceptions: (opts?: PageOpts) => client.resource<ReconException>('recon-exceptions').list(page(opts)),
    resolveReconException: (id: string, status: 'resolved' | 'ignored') => post<ReconException>(`/v1/recon-exceptions/${id}/resolve`, { status }),

    // ── Stats (server-side dashboard aggregates) ──────────────────────────
    getStats: () => client.request<Stats>('/v1/stats'),

    // ── Team (invites + members) ──────────────────────────────────────────
    listMembers: () => client.request<{ data: Member[] }>('/v1/members'),
    listInvites: () => client.request<{ data: TenantInvite[] }>('/v1/invites'),
    createInvite: (body: { email: string; role: string }) => post<TenantInvite & { invite_url: string }>('/v1/invites', body),

    // ── API keys ──────────────────────────────────────────────────────────
    listApiKeys: () => client.request<{ data: { id: string; name: string; key_prefix: string; created_at: string }[] }>('/v1/api-keys'),
    createApiKey: (body: { name: string; expires_at?: string }) => post<{ id: string; key_prefix: string; api_key: string }>('/v1/api-keys', body),
    revokeApiKey: (id: string) => del<{ id: string; revoked: boolean }>(`/v1/api-keys/${id}`),

    // ── Reports (raw metric-view rows) ────────────────────────────────────
    getReport: <T>(name: string) => client.request<{ data: T[] }>(`/v1/reports/${name}`),
  };
}

export type Api = ReturnType<typeof createApi>;
export type { EngineClient };
